import { utcToZonedTime, format as tzFormat } from 'date-fns-tz';
import hereRouting from '@/helpers/here/routing';
import { encode, decode } from '@/helpers/here/flexipolyline';
import { toLatLng, calculateDepartureTime, nextWeekTodayOrWeekday } from '@/modules/Flexigo/helpers/utility';
import { timeStrToInt, extractTimeFromISO } from '@/helpers/dateTime';
import { cloneObject } from '../../../../helpers/utility';
import { getIcon, MarkerCategory } from '../../constants/mapMarkers';

export function getMapIcon(id, type, otp, routeState, nextStop) {
  if (type === 'campus' && otp === false) {
    // { stroke: '#cb3e4f', fill: '#cb3e4f', comment: 'late' };
    return 'pin-campus-anomaly.svg';
  }
  if (type === 'campus' && otp === true) {
    // { stroke: '#1eb64d', fill: '#1eb64d', comment: 'on time' };
    return 'pin-campus-ok.svg';
  }

  if (type === 'campus' && otp === null) {
    // { stroke: '#1eb64d', fill: '#1eb64d', comment: 'on time' };
    return 'pin-campus.svg';
  }

  // return {
  //     stroke: '#CB3E4F', fill: 'white', strokeWidth: 2, strokeDasharray: '3, 3', textFill: '#CB3E4F', comment: 'next',
  //   };

  if (id === nextStop?.id) {
    const stroke = routeState === 'ON_THE_WAY_WITH_DELAY' ? '#CB3E4F' : '#50DE7B';
    const textFill = routeState === 'ON_THE_WAY_WITH_DELAY' ? '#CB3E4F' : '#50DE7B';
    return {
      stroke,
      fill: 'white',
      strokeWidth: 1,
      strokeDasharray: '3, 3',
      textFill,
      comment: 'next',
    };
  }
  if (otp === false) {
    return { stroke: '', fill: '#CB3E4F', comment: 'late' };
  }

  if (otp === true) {
    return { stroke: '', fill: '#50DE7B', comment: 'on time' };
  }

  return { stroke: 'white', fill: '#7F8FA4', comment: 'default' };
}

export function convertToZonedTime(date, timeZone) {
  if (timeZone && timeZone !== 'UTC') {
    // Convert the UTC date to the same instant in the target timeZone
    const zonedDate = utcToZonedTime(date, timeZone);

    // Format the zoned date to ISO 8601 string
    const pattern = "yyyy-MM-dd'T'HH:mm:ssXXX";
    const formattedDateWithOffset = tzFormat(zonedDate, pattern, { timeZone });
    return formattedDateWithOffset;
  }
  return date.toISOString();
}

export function orderStations(order, stations) {
  if (!order || !stations) return [];
  const orderedStations = stations.reduce((acc, cur) => {
    acc[cur.id] = cur;
    return acc;
  }, {});
  return order.map((o) => orderedStations[o]).filter((el) => el);
}

export function getMarkerPosition(p) {
  return {
    lat: Number(p.homeLocation?.latitude) || 0,
    lng: Number(p.homeLocation?.longitude) || 0,
  };
}

export function orderStationsByWaypoints(stations, waypointsOrder, fromType = 'CAMPUS') {
  if (!waypointsOrder) return stations;
  if (fromType !== 'CAMPUS') {
    const waypoints = waypointsOrder.map((order) => stations.slice(1)[order]);
    return [stations[0], ...waypoints];
  }
  const waypoints = waypointsOrder.map((order) => stations[order]);
  waypoints.push(stations[stations.length - 1]);
  return waypoints;
}

export function prepareStationMarkerData(station, event = null) {
  if (!station) return {};
  const iconPath = getIcon(MarkerCategory.STATION, event);
  return {
    ...station,
    stationName: station.description,
    address: station.location.address,
    position: {
      lat: station.location.latitude,
      lng: station.location.longitude,
    },
    icon: {
      url: `${process.env.BASE_URL}${iconPath}`,
      labelOrigin: { x: 13, y: 15 },
    },
    animation: 2,
    visible: true,
    infoData: {
      serviceLine: station.description,
      personnel: station.personnelIds?.length || 0,
    },
  };
}

/*
  directionsService: google.maps.DirectionsService
  coords: { origin: { lat: 41.015137, lng: 28.979530 }, destination: { lat: 41.008633, lng: 28.978360 }, waypoints: [{ location: { lat: 41.008633, lng: 28.978360 } }] }
  drivingOptions: { departureTime: new Date(), trafficModel: 'pessimistic' }
  travelMode: 'DRIVING'
  provideRouteAlternatives: false
*/
export async function getDirections(
  directionsService,
  coords,
  drivingOptions,
  travelMode = 'DRIVING',
  provideRouteAlternatives = false,
) {
  const { origin, destination, waypoints } = coords;
  const request = {
    origin,
    destination,
    waypoints,
    travelMode,
    provideRouteAlternatives,
  };

  if (drivingOptions) request.drivingOptions = drivingOptions;

  return new Promise((resolve, reject) => {
    directionsService.route(request, (result, status) => {
      if (status === 'OK') {
        resolve(result);
      } else {
        reject(status);
      }
    });
  });
}

export async function getDirectionsHere(coords, time = {}, timezone = 'UTC') {
  const hereRouter = hereRouting();
  const { origin, destination, waypoints } = coords;

  const request = {
    origin: `${origin.lat},${origin.lng}`,
    destination: `${destination.lat},${destination.lng}`,
    via: waypoints,
    transportMode: 'car',
    routingMode: 'fast',
    alternatives: 0,
  };

  if (time.departureTime) request.departureTime = convertToZonedTime(time.departureTime, timezone);
  if (!time.departureTime && time.arrivalTime) request.arrivalTime = convertToZonedTime(time.arrivalTime, timezone);

  const r = await hereRouter.calculateRoute(request);

  return r;
}

export function completeRouteSummary(sections) {
  const totalSummary = {
    encodedPolyline: '',
    duration: 0,
    length: 0,
    path: [],
    tollCost: 0,
  };

  const sum = sections.reduce((acc, cur) => {
    const { summary, polyline } = cur;
    const { duration, length } = summary;
    const { polyline: decodedPolyline } = decode(polyline);
    acc.length += length;
    acc.duration += duration;

    // Validate coordinates before adding to path
    const validCoordinates = decodedPolyline
      .map((p) => ({ lat: p[0], lng: p[1], original: p }))
      .filter(({ lat, lng, original }) => {
        const isValid = typeof lat === 'number' && typeof lng === 'number' && !Number.isNaN(lat) && !Number.isNaN(lng);
        if (!isValid) {
          console.warn('⚠️ [completeRouteSummary] Invalid coordinate detected:', { lat, lng, original });
        }
        return isValid;
      })
      .map(({ lat, lng }) => [lat, lng]);

    acc.path = [...acc.path, ...validCoordinates];
    return acc;
  }, totalSummary);

  sum.encodedPolyline = encode({ polyline: sum.path });
  return sum;
}

export function getWaypoints(isInbound, allStops, campusLocations, stopDuration = 0, isSube = false) {
  // When campuses array is empty, we need to exclude both first and last stations from waypoints
  // because they are used as origin and destination
  const hasCampuses = campusLocations && campusLocations.length > 0;

  let stopsForNotSube;
  if (hasCampuses) {
    // Original logic: when campuses exist, exclude only one end
    stopsForNotSube = isInbound ? allStops.slice(1, allStops.length) : allStops.slice(0, allStops.length - 1);
  } else {
    // When no campuses: exclude both first and last stations (they become origin/destination)
    stopsForNotSube = allStops.slice(1, allStops.length - 1);
  }

  const stops = isSube ? allStops.slice(1, -1) : stopsForNotSube;
  const campuses = isInbound
    ? campusLocations.slice(0, campusLocations.length - 1)
    : campusLocations.slice(1, campusLocations.length);
  const stopWaypoints = stops
    .map((el) => {
      const { lat, lng } = toLatLng(el);
      const passThrough = el.isPassthrough || false;
      return {
        lat,
        lng,
        stopDuration,
        passThrough,
      };
    })
    .filter((item) => item.lat && item.lng);
  const campusWaypoints = campuses
    .map((el) => ({ lat: el.position.lat, lng: el.position.lng, stopDuration }))
    .filter((item) => item.lat && item.lng);
  const combinedWaypoints = isInbound ? [...stopWaypoints, ...campusWaypoints] : [...campusWaypoints, ...stopWaypoints];
  return isSube ? stopWaypoints : combinedWaypoints;
}

export function cleanPersonnelObject(personnel) {
  // Add null check for personnel parameter
  if (!personnel || !personnel.personnel) {
    console.warn('Invalid personnel object in cleanPersonnelObject');
    return null;
  }

  return {
    ...personnel.personnel,
    routeId: personnel?.route?.id,
    routeName: personnel?.route?.name,
    stationName: personnel?.station?.name,
    stationDescription: personnel?.station?.description,
  };
}

export function decodePolyline(encodedPolyline) {
  if (!encodedPolyline) return [];
  const decoded = decode(encodedPolyline);
  return decoded.polyline.map((a) => ({ lat: a[0], lng: a[1] }));
}

// Helper: Create station data object for passthrough stations
function createPassthroughStationData(station, index, cumulativeDistance) {
  return {
    id: station.id,
    index,
    expectedArrivalHour: undefined,
    expectedDepartureHour: undefined,
    distance: cumulativeDistance,
    duration: 0,
  };
}

// Helper: Create station data object for regular stations
function createRegularStationData(station, index, times, cumulativeDistance, duration) {
  return {
    id: station.id,
    index,
    expectedArrivalHour: times.expectedArrivalHour,
    expectedDepartureHour: times.expectedDepartureHour,
    distance: cumulativeDistance,
    duration,
  };
}

// Helper: Calculate times for inbound first station
function calculateInboundFirstStationTimes(section, serviceTimeForStopInSeconds) {
  // Validate section exists and has required data
  if (!section || !section.departure || !section.departure.time) {
    console.error('calculateInboundFirstStationTimes: Invalid section data');
    return { expectedArrivalHour: undefined, expectedDepartureHour: undefined };
  }

  const departureTime = extractTimeFromISO(section.departure.time);

  // Validate extracted time
  if (!departureTime) {
    console.error('calculateInboundFirstStationTimes: Failed to extract departure time from ISO');
    return { expectedArrivalHour: undefined, expectedDepartureHour: undefined };
  }

  const [depHours, depMinutes] = departureTime.split(':').map(Number);

  const totalMinutes = depHours * 60 + depMinutes;
  const serviceTimeInMinutes = Math.round(serviceTimeForStopInSeconds / 60);
  let arrivalTotalMinutes = totalMinutes - serviceTimeInMinutes;

  // Handle edge case: if subtraction results in negative time (crossing to previous day)
  // Add 24 hours (1440 minutes) to wrap around to previous day
  if (arrivalTotalMinutes < 0) {
    arrivalTotalMinutes += 1440; // 24 * 60 = 1440 minutes
  }

  const arrivalHours = Math.floor(arrivalTotalMinutes / 60);
  const arrivalMinutes = Math.round(arrivalTotalMinutes % 60);

  const arrivalTimeStr = `${String(arrivalHours).padStart(2, '0')}:${String(arrivalMinutes).padStart(2, '0')}`;

  return {
    expectedArrivalHour: timeStrToInt(arrivalTimeStr),
    expectedDepartureHour: timeStrToInt(departureTime),
  };
}

// Helper: Calculate times for inbound regular stations
function calculateInboundRegularStationTimes(currentSection, previousSection) {
  // Validate sections exist and have required data
  if (!previousSection || !previousSection.arrival || !previousSection.arrival.time) {
    console.error('calculateInboundRegularStationTimes: Invalid previous section data');
    return { expectedArrivalHour: undefined, expectedDepartureHour: undefined };
  }

  if (!currentSection || !currentSection.departure || !currentSection.departure.time) {
    console.error('calculateInboundRegularStationTimes: Invalid current section data');
    return { expectedArrivalHour: undefined, expectedDepartureHour: undefined };
  }

  const arrivalTime = extractTimeFromISO(previousSection.arrival.time);
  const departureTime = extractTimeFromISO(currentSection.departure.time);

  // Validate extracted times
  if (!arrivalTime || !departureTime) {
    console.error('calculateInboundRegularStationTimes: Failed to extract times from ISO');
    return { expectedArrivalHour: undefined, expectedDepartureHour: undefined };
  }

  return {
    expectedArrivalHour: timeStrToInt(arrivalTime),
    expectedDepartureHour: timeStrToInt(departureTime),
  };
}

// Helper: Calculate times for outbound stations
function calculateOutboundStationTimes(sections, sectionIndex, isLastStation) {
  // Input validation
  if (!sections || !Array.isArray(sections) || sections.length === 0) {
    console.error('calculateOutboundStationTimes: Invalid sections array');
    return { expectedArrivalHour: undefined, expectedDepartureHour: undefined };
  }

  // Bounds checking for sectionIndex
  if (sectionIndex < 0 || sectionIndex >= sections.length) {
    console.error('calculateOutboundStationTimes: sectionIndex out of bounds', {
      sectionIndex,
      sectionsLength: sections.length,
    });
    return { expectedArrivalHour: undefined, expectedDepartureHour: undefined };
  }

  const arrivalSection = sections[sectionIndex];

  // Validate arrival section structure
  if (!arrivalSection || !arrivalSection.arrival || !arrivalSection.arrival.time) {
    console.error('calculateOutboundStationTimes: Invalid arrival section data', {
      sectionIndex,
      hasSection: !!arrivalSection,
      hasArrival: !!arrivalSection?.arrival,
      hasTime: !!arrivalSection?.arrival?.time,
    });
    return { expectedArrivalHour: undefined, expectedDepartureHour: undefined };
  }

  const arrivalTime = extractTimeFromISO(arrivalSection.arrival.time);
  if (!arrivalTime) {
    console.error('calculateOutboundStationTimes: Failed to extract arrival time from ISO', {
      isoString: arrivalSection.arrival.time,
    });
    return { expectedArrivalHour: undefined, expectedDepartureHour: undefined };
  }

  const expectedArrivalHour = timeStrToInt(arrivalTime);

  let expectedDepartureHour;
  if (!isLastStation) {
    // Bounds checking for next section
    const nextSectionIndex = sectionIndex + 1;
    if (nextSectionIndex < sections.length) {
      const departureSection = sections[nextSectionIndex];

      // Validate departure section structure
      if (departureSection && departureSection.departure && departureSection.departure.time) {
        const departureTime = extractTimeFromISO(departureSection.departure.time);
        if (departureTime) {
          expectedDepartureHour = timeStrToInt(departureTime);
        } else {
          console.warn('calculateOutboundStationTimes: Failed to extract departure time from ISO', {
            sectionIndex: nextSectionIndex,
            isoString: departureSection.departure.time,
          });
        }
      } else {
        console.warn('calculateOutboundStationTimes: Invalid departure section data', {
          sectionIndex: nextSectionIndex,
          hasSection: !!departureSection,
          hasDeparture: !!departureSection?.departure,
          hasTime: !!departureSection?.departure?.time,
        });
      }
    } else {
      console.warn('calculateOutboundStationTimes: Next section index out of bounds', {
        nextSectionIndex,
        sectionsLength: sections.length,
      });
    }
  }

  return { expectedArrivalHour, expectedDepartureHour };
}

// Helper: Process a single regular station
function processRegularStation({
  station,
  stationIndex,
  sections,
  sectionIndex,
  processedStations,
  stationCount,
  cumulativeDistance,
  isInbound,
  serviceTimeForStopInSeconds,
}) {
  const isFirstStation = processedStations === 0;
  const isLastStation = processedStations === stationCount - 1;

  let times;
  let duration;
  let distance;

  if (isInbound) {
    // Inbound: use distance/duration from current section
    distance = sections[sectionIndex]?.distance || 0;
    duration = sections[sectionIndex]?.duration || 0;

    if (isFirstStation) {
      // First station is origin: calculate arrival from departure minus service time
      times = calculateInboundFirstStationTimes(sections[0], serviceTimeForStopInSeconds);
    } else {
      // Other stations: arrival from previous section, departure from current section
      times = calculateInboundRegularStationTimes(sections[sectionIndex], sections[sectionIndex - 1]);
    }
  } else {
    // Outbound: use distance/duration from arrival section
    const arrivalSection = sections[sectionIndex];
    distance = arrivalSection?.summary?.length || 0;
    duration = arrivalSection?.summary?.duration || 0;

    // All stations: arrival from current section, departure from next (unless last)
    times = calculateOutboundStationTimes(sections, sectionIndex, isLastStation);
  }

  return {
    stationData: createRegularStationData(station, stationIndex, times, cumulativeDistance + distance, duration),
    distance,
  };
}

export function calculateStationTimesHelper(
  stationsArray,
  sections,
  serviceTimeForStopInSeconds,
  isInbound = true,
  campusCount = 0,
) {
  const calculatedStations = [];
  const regularStations = stationsArray.filter((station) => !station.isPassthrough);

  // Calculate how many sections are campus-to-campus (to be skipped)
  // For multi-campus routes: N campuses create N-1 campus-to-campus sections
  const campusSections = campusCount > 1 ? campusCount - 1 : 0;
  const availableSections = sections.length - campusSections;

  // Calculate how many stations we can process
  // Inbound: sections match station count (1 section per station)
  // Outbound: N stations need N-1 sections, so we can process availableSections + 1 stations
  // (first station uses section 0 for departure, last station uses last section for arrival)
  const stationCount = isInbound
    ? Math.min(availableSections, regularStations.length)
    : Math.min(availableSections + 1, regularStations.length);

  // Configure section indexing based on route direction
  // Inbound: campus sections at END, start from index 0
  // Outbound: campus sections at START, skip them by starting at campusSections
  let sectionIndex = isInbound ? 0 : campusSections;
  let processedStations = 0;
  let cumulativeDistance = 0;

  // Process all stations (passthrough and regular)
  for (let i = 0; i < stationsArray.length; i += 1) {
    const station = stationsArray[i];

    if (station.isPassthrough) {
      // Passthrough stations don't affect timing
      calculatedStations.push(createPassthroughStationData(station, i, cumulativeDistance));
    } else if (processedStations < stationCount) {
      // Regular station: calculate times from sections
      const { stationData, distance } = processRegularStation({
        station,
        stationIndex: i,
        sections,
        sectionIndex,
        processedStations,
        stationCount,
        cumulativeDistance,
        isInbound,
        serviceTimeForStopInSeconds,
      });

      calculatedStations.push(stationData);
      cumulativeDistance += distance;
      sectionIndex += 1;
      processedStations += 1;
    } else {
      // Stations beyond stationCount are treated as passthrough
      calculatedStations.push(createPassthroughStationData(station, i, cumulativeDistance));
    }
  }

  return calculatedStations;
}

export function mappedRouteData(versionData) {
  // Add null check for versionData parameter
  if (!versionData) {
    console.error('versionData is null or undefined in mappedRouteData');
    return null;
  }

  // Support both draft and active version structures
  const version = versionData.draftVersion || versionData.activeVersion;
  const routeDetail = version?.routeDetail;

  if (!routeDetail) {
    console.error('Route detail is not available in the stored version data.');
    return null;
  }

  const newRoutesData = {
    TO: {
      path: routeDetail.path?.encodedPolyline ? decodePolyline(routeDetail.path.encodedPolyline) : [],
      personnelStations: version.routeDetail?.pathPersonnelStations || {},
      distanceInKm: routeDetail.path?.distanceInKm,
      durationInMin: routeDetail.path?.durationInMin,
      encodedPolyline: routeDetail.path?.encodedPolyline,
      stopIds: routeDetail.path?.lstRouteStopIds || [],
    },
    FROM: {
      path: routeDetail.returnPath?.encodedPolyline ? decodePolyline(routeDetail.returnPath.encodedPolyline) : [],
      personnelStations: version.routeDetail?.returnPathPersonnelStations || {},
      distanceInKm: routeDetail.returnPath?.distanceInKm,
      durationInMin: routeDetail.returnPath?.durationInMin,
      encodedPolyline: routeDetail.returnPath?.encodedPolyline,
      stopIds: routeDetail.returnPath?.lstRouteStopIds || [],
    },
  };

  return newRoutesData;
}

/**
 * Build routesData (TO/FROM) from the route-details endpoint (`rest/v3/routes/{id}`)
 * instead of the version endpoint. Used for loop (RING) routes — identified by a
 * truthy `routeDetails.ringId` — which cannot be edited and may have their geometry
 * created directly on the BE. Their path comes from `routeDetails.routePath` rather
 * than a draft version, preventing them from appearing as draft routes.
 *
 * `routeDetails` shape (from `rest/v3/routes/{id}`):
 *   routePath:        { encodedPolyline, pathCoordinates, stationIds, distanceInKm, distanceInMin, ... }
 *   returnRoutePath:  { encodedPolyline, pathCoordinates, stationIds, distanceInKm, distanceInMin, ... }
 */
export function mappedRouteDataFromRouteDetails(routeDetails) {
  if (!routeDetails) {
    console.error('routeDetails is null/undefined in mappedRouteDataFromRouteDetails');
    return null;
  }

  const { routePath, returnRoutePath } = routeDetails;

  if (!routePath && !returnRoutePath) {
    console.error('No routePath/returnRoutePath available in routeDetails.');
    return null;
  }

  const buildDirection = (path) => {
    if (!path) {
      return { path: [], stopIds: [] };
    }
    // Prefer encodedPolyline; fall back to pathCoordinates ([lat, lng] pairs).
    let decodedPath = [];
    if (path.encodedPolyline) {
      decodedPath = decodePolyline(path.encodedPolyline);
    } else if (Array.isArray(path.pathCoordinates)) {
      decodedPath = path.pathCoordinates.map(([lat, lng]) => ({ lat, lng }));
    }
    return {
      path: decodedPath,
      distanceInKm: path.distanceInKm,
      durationInMin: path.distanceInMin,
      encodedPolyline: path.encodedPolyline,
      stopIds: path.stationIds || path.lstRouteStopIds || [],
    };
  };

  return {
    TO: buildDirection(routePath),
    FROM: buildDirection(returnRoutePath),
  };
}

export function getUpdatedRouteDetails(
  versionData,
  routesDataTo,
  routesDataFrom,
  listOfStationsTo,
  listOfStationsFrom,
  isRoundTrip,
) {
  // Add null check for versionData
  if (!versionData || !versionData.draftVersion) {
    console.error('versionData or versionData.draftVersion is null/undefined in getUpdatedRouteDetails');
    return null;
  }

  const draftVersionData = cloneObject(versionData.draftVersion);

  const getRoutesData = (direction) => {
    const routeData = direction === 'TO' ? routesDataTo : routesDataFrom;
    const stations = direction === 'TO' ? listOfStationsTo : listOfStationsFrom;

    const { distanceInKm, durationInMin, encodedPolyline, sections } = routeData;
    const personnelStations = routeData.personnelStations || [];
    const lstRouteStopIds = stations.filter((route) => !route.isPassthrough).map((item) => item.id) || [];

    return {
      pathData: {
        distanceInKm,
        durationInMin,
        encodedPolyline,
        sections,
        lstRouteStopIds,
      },
      personnelStations,
    };
  };
  const toData = getRoutesData('TO');

  draftVersionData.routeDetail.path = {
    ...draftVersionData.routeDetail.path,
    ...toData.pathData,
  };
  draftVersionData.routeDetail.pathPersonnelStations = toData.personnelStations;

  if (isRoundTrip) {
    const fromData = getRoutesData('FROM');
    draftVersionData.routeDetail.returnPath = {
      ...draftVersionData.routeDetail.returnPath,
      ...fromData.pathData,
    };
    draftVersionData.routeDetail.returnPathPersonnelStations = fromData.personnelStations;
  } else {
    // Clear return path data when not round trip to avoid stale data
    draftVersionData.routeDetail.returnPath = {
      ...draftVersionData.routeDetail.returnPath,
      lstRouteStopIds: [],
      distanceInKm: undefined,
      durationInMin: undefined,
      encodedPolyline: undefined,
      sections: undefined,
    };
    draftVersionData.routeDetail.returnPathPersonnelStations = undefined;
  }

  draftVersionData.updateTime = new Date().getTime();
  return draftVersionData;
}

export function mappedStationData(
  { expectedArrivalHour, expectedDepartureHour, id: stationId },
  templateRouteDirection,
) {
  return {
    expectedArrivalHour: expectedArrivalHour || undefined,
    expectedDepartureHour: expectedDepartureHour || undefined,
    stationId,
    templateRouteDirection: ['FIRST_LEG', 'RETURN_LEG'].includes(templateRouteDirection)
      ? templateRouteDirection
      : 'FIRST_LEG', // Default to FIRST_LEG if not specified
  };
}

export function formatStationStatsData({
  draftId,
  stationId,
  expectedArrivalHour,
  expectedDepartureHour,
  templateRouteDirection,
}) {
  if (!draftId || !stationId) return {};
  return {
    [draftId]: [
      {
        expectedArrivalHour,
        expectedDepartureHour,
        stationId,
        templateRouteDirection, // 'FIRST_LEG' or 'RETURN_LEG'
      },
    ],
  };
}

export function getRouteCoordinates({ stations, campuses, isInbound = false, isSube = false, stopDuration = 0 }) {
  if (!stations || stations.length === 0) return null;

  const combinedStops = isInbound ? [...stations, ...campuses] : [...campuses, ...stations];
  const stopsOnRoute = isSube ? stations : combinedStops;

  const origin = {
    lat: stopsOnRoute[0]?.location?.latitude || 0,
    lng: stopsOnRoute[0]?.location?.longitude || 0,
  };

  const destination = {
    lat: stopsOnRoute[stopsOnRoute.length - 1]?.location?.latitude || 0,
    lng: stopsOnRoute[stopsOnRoute.length - 1]?.location?.longitude || 0,
  };

  const waypoints = getWaypoints(isInbound, stations, campuses, stopDuration, isSube);

  return {
    origin,
    destination,
    waypoints,
  };
}

/**
 * Calculate route stop times using HERE API
 * @param {Array} stations - Array of stations with locations (includes both regular stations and campuses)
 * @param {Object} timePayload - Time payload with departureTime or arrivalTime
 * @param {number} serviceTimeForStopInSeconds - Service time at each stop in seconds
 * @param {string} timezone - Timezone for the route (default: 'UTC')
 * @param {boolean} isInbound - Whether the route is inbound (default: false)
 * @returns {Promise<Array>} - Array of calculated stations with expectedArrivalHour and expectedDepartureHour
 */
export async function calculateRouteStopTimes(
  stations,
  timePayload,
  serviceTimeForStopInSeconds = 0,
  timezone = 'UTC',
  isInbound = false,
) {
  if (!stations || stations.length === 0) {
    throw new Error('No stations provided');
  }

  // Prepare route coordinates - stations are already in order, pass directly
  const routeCoordinates = getRouteCoordinates({
    stations,
    campuses: [],
    isInbound,
    isSube: false,
    stopDuration: serviceTimeForStopInSeconds,
  });

  if (!routeCoordinates) {
    throw new Error('Failed to get route coordinates');
  }

  // Call HERE API to get route sections
  const response = await getDirectionsHere(routeCoordinates, timePayload, timezone);
  const { sections } = response.routes[0] || {};

  if (!sections || sections.length === 0) {
    throw new Error('No sections returned from HERE API');
  }

  // stations includes all the stations and campuses,
  // origin campus is not included in the calculation
  // calculation should include the other campuses unlike route calculations
  const stopsWithoutOriginCampus = isInbound ? stations.slice(0, -1) : stations.slice(1);

  // Calculate station times using the helper function
  // sections are skipped for campusCount since they shouldnt be updated
  // campusCount is 1 because we only include the origin campus
  const calculatedStations = calculateStationTimesHelper(
    stopsWithoutOriginCampus,
    sections,
    serviceTimeForStopInSeconds,
    isInbound,
    1,
  );

  return calculatedStations;
}

/**
 * Build coordinates for campus-only routes
 * @param {Array} campuses - Array of campus objects with location
 * @param {number} serviceTimeInMin - Service time per stop in minutes
 * @returns {Object} - Route coordinates with origin, destination, and waypoints
 */
function buildCampusCoordinates(campuses, serviceTimeInMin) {
  if (!campuses || campuses.length === 0) {
    throw new Error('No campuses provided');
  }

  const origin = campuses[0];
  const destination = campuses[campuses.length - 1];

  return {
    origin: {
      lat: origin.location.latitude,
      lng: origin.location.longitude,
    },
    destination: {
      lat: destination.location.latitude,
      lng: destination.location.longitude,
    },
    waypoints:
      campuses.length > 2
        ? campuses.slice(1, -1).map((campus) => ({
            id: campus.id,
            lat: campus.location.latitude,
            lng: campus.location.longitude,
            stopDuration: serviceTimeInMin * 60,
          }))
        : [],
  };
}

/**
 * Calculate campus arrival and departure times from route sections
 * @param {Array} campuses - Array of campus objects
 * @param {Array} sections - Route sections from HERE API
 * @param {number} serviceTimeInSeconds - Service time per stop in seconds
 * @returns {Array} - Array of campus time data
 */
function calculateCampusTimes(campuses, sections, serviceTimeInSeconds) {
  return campuses.map((campus, i) => {
    let arrivalTime;
    let departureTime;
    let readyTime;

    if (i === 0) {
      // First campus: calculate arrival by subtracting service time from departure
      const departureDate = new Date(sections[0].departure.time);
      const arrivalDate = new Date(departureDate.getTime() - serviceTimeInSeconds * 1000);
      arrivalTime = arrivalDate.toTimeString().slice(0, 5);
      departureTime = departureDate.toTimeString().slice(0, 5);
      readyTime = arrivalDate.toTimeString().slice(0, 5);
    } else {
      // Other campuses: arrival from previous section
      const arrivalDate = new Date(sections[i - 1].arrival.time);
      arrivalTime = arrivalDate.toTimeString().slice(0, 5);
      readyTime = arrivalDate.toTimeString().slice(0, 5);

      // Departure from current section (if exists)
      if (sections[i]?.departure?.time) {
        const departureDate = new Date(sections[i].departure.time);
        departureTime = departureDate.toTimeString().slice(0, 5);
      }
    }

    return {
      campusId: campus.id,
      campusIndex: i,
      arrivalTime,
      departureTime,
      readyTime,
      departure: sections[i]?.departure,
      arrival: sections[i - 1]?.arrival,
    };
  });
}

/**
 * Get merged polyline from route sections
 * @param {Array} sections - Route sections from HERE API
 * @returns {string} - Encoded polyline string
 */
function getPolylineFromSections(sections) {
  let mergedPolyline = [];
  sections.forEach((section) => {
    if (section.polyline) {
      const p = decode(section.polyline);
      mergedPolyline = [...mergedPolyline, ...p.polyline];
    }
  });

  return encode({ polyline: mergedPolyline });
}

/**
 * Calculate route with campus times
 * Main utility function for campus-only route calculations
 * @param {Object} config - Configuration object
 * @param {Array} config.campuses - Array of campus objects with location
 * @param {boolean} config.isInbound - Direction flag (true for TO, false for FROM)
 * @param {number} config.serviceTimeInMin - Service time per stop in minutes
 * @param {string} config.requestTime - Base time (arrival or departure) in HHMM format
 * @param {string} config.timezone - Timezone for calculations (default: 'UTC')
 * @returns {Promise<Object>} - Calculated route data with sections, campusTimes, polyline, and summary
 */
export async function calculateCampusRoute({ campuses, isInbound, serviceTimeInMin, requestTime, timezone = 'UTC' }) {
  if (!campuses || campuses.length === 0) {
    throw new Error('No campuses provided');
  }

  if (!requestTime) {
    throw new Error('Request time is required');
  }

  // 1. Build coordinates
  const coords = buildCampusCoordinates(campuses, serviceTimeInMin);

  // 2. Prepare time payload
  const timeValue = typeof requestTime === 'string' ? timeStrToInt(requestTime) : requestTime;
  const calculatedTime = calculateDepartureTime(timeValue);

  const timePayload = isInbound
    ? { arrivalTime: calculatedTime, departureTime: undefined }
    : { arrivalTime: undefined, departureTime: calculatedTime };

  // 3. Call HERE API
  const response = await getDirectionsHere(coords, timePayload, timezone);
  const { sections } = response.routes[0] || {};

  if (!sections || sections.length === 0) {
    throw new Error('No sections returned from HERE API');
  }

  // 4. Calculate campus times
  const serviceTimeInSeconds = Math.round(serviceTimeInMin * 60);
  const campusTimesData = calculateCampusTimes(campuses, sections, serviceTimeInSeconds);

  // 5. Get polyline
  const polyline = getPolylineFromSections(sections);

  // 6. Get route summary
  const summary = completeRouteSummary(sections);

  return {
    sections,
    campusTimesData,
    polyline,
    summary,
  };
}

/**
 * Build the time payload ({ arrivalTime } or { departureTime }) for a HERE
 * Routing API call, using the campus timezone for correct weekday resolution.
 *
 * @param {Object}  options
 * @param {Object}  options.template        - workgroup template (shift, direction, …)
 * @param {boolean} options.isInbound       - true when the route direction is inbound
 * @param {string}  options.routeDirection  - 'TO' | 'FROM'
 * @param {string}  [options.campusTimezone] - IANA timezone of the campus (default 'UTC')
 * @returns {Object} e.g. { arrivalTime: Date } or { departureTime: Date }
 */
export function getRouteTimeParams({ template, isInbound, routeDirection, campusTimezone }) {
  if (!template || !template.shift) {
    return isInbound ? { arrivalTime: null } : { departureTime: null };
  }

  const { shift } = template;
  const arrivalTimeSource = routeDirection === 'TO' ? shift.arrivalHour : shift.returnArrivalHour;
  const departureTimeSource = routeDirection === 'TO' ? shift.departureHour : shift.returnDepartureHour;

  const arrivalTime = arrivalTimeSource ? nextWeekTodayOrWeekday(arrivalTimeSource, campusTimezone) : null;
  const departureTime = departureTimeSource ? nextWeekTodayOrWeekday(departureTimeSource, campusTimezone) : null;

  return isInbound ? { arrivalTime } : { departureTime };
}

/**
 * Creates a curved arc path between two geographic coordinates using a quadratic Bezier curve
 * @param {Object} start - Starting coordinate {lat, lng}
 * @param {Object} end - Ending coordinate {lat, lng}
 * @param {number} numPoints - Number of points to generate along the arc (default: 50)
 * @param {number} arcHeightRatio - Arc height as ratio of distance (default: 0.2 = 20%)
 * @returns {Array} Array of coordinate objects {lat, lng} forming the arc path
 */
export function createArcPath(start, end, numPoints = 50, arcHeightRatio = 0.2) {
  const points = [];

  // Calculate the distance for arc height (perpendicular offset)
  const latDiff = end.lat - start.lat;
  const lngDiff = end.lng - start.lng;
  const distance = Math.sqrt(latDiff * latDiff + lngDiff * lngDiff);

  // Handle identical or very close points (prevent division by zero)
  if (distance < 0.0001) {
    return [start, end];
  }

  // Calculate the midpoint
  const midLat = (start.lat + end.lat) / 2;
  const midLng = (start.lng + end.lng) / 2;

  // Arc height based on ratio
  const arcHeight = distance * arcHeightRatio;

  // Perpendicular direction (rotated 90 degrees)
  const perpLat = -lngDiff / distance;
  const perpLng = latDiff / distance;

  // Control point for the arc (offset from midpoint)
  const controlLat = midLat + perpLat * arcHeight;
  const controlLng = midLng + perpLng * arcHeight;

  // Generate points along a quadratic Bezier curve
  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints;
    const oneMinusT = 1 - t;

    // Quadratic Bezier formula: B(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
    const lat = oneMinusT * oneMinusT * start.lat + 2 * oneMinusT * t * controlLat + t * t * end.lat;
    const lng = oneMinusT * oneMinusT * start.lng + 2 * oneMinusT * t * controlLng + t * t * end.lng;

    points.push({ lat, lng });
  }

  return points;
}

export default orderStations;
